import React, { useEffect, useRef, useState } from 'react'
import AppLayout from '../../../../components/layout/AppLayout'
import { useLocation, useNavigate } from 'react-router-dom';
import { useObjectsApi } from '../../../../api/objects/objects';
import { TLesson, TLessonMetadata } from '../../../../models/courses/lesson';
import { Alert, Card, Col, Collapse, CollapseProps, message, Row, Button } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { TCharacter, TObstacle, TScene, TTarget } from '../../../../models/objects/objects';
import { useCourseApi } from '../../../../api/courses/courses';
import Character from '../lessonBlockly/Character';
import Scene from '../lessonBlockly/Scene';
import Target from '../lessonBlockly/Target';
import Obstacle from '../lessonBlockly/Obstacle';
import { TLessonCharacterTOTCharacter } from '../../../../utils/blockly/character';
import { useReferential } from '../../../../context/ReferentialContext';
import { TLessonTargetsTOTTargets } from '../../../../utils/blockly/target';
import { TLessonSceneTOScene } from '../../../../utils/blockly/scene';
import { TLessonObstaclesTOObstacles } from '../../../../utils/blockly/obstacle';
import BlocklyGame from '../../../../blocks/core/component/BlocklyGame/BlocklyGame';
import BlocklyContent from '../lessonBlockly/BlocklyContent';
import { CODIFICATION_LESSON_METADATA, CODIFICATION_TYPE } from '../../../../constant/codification';
import { TLessonBlocklyContentTOBlocklyBlocks } from '../../../../utils/blockly/blocklyfields';
import MetaData from '../lessonBlockly/MetaData';
import { stringToBool } from '../../../../utils/format/valueFormat';


const LessonBlocklyGame: React.FC = () => {
    const location = useLocation();
    const { getCharacters, getObstacles, getScenes, getTargets } = useObjectsApi();
    const { getLessonDetailsById, saveLessonDetails } = useCourseApi();
    const { getCodificationIdByCodes, getCodificationsByCodeType, getCodificationCodeById, isReferentialLoaded } = useReferential();

    const [loading, setIsLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);

    const [characters, setCharacters] = useState<TCharacter[]>([]);

    const [obstacles, setObstacles] = useState<TObstacle[]>([]);

    const [scenes, setScenes] = useState<TScene[]>([]);

    const [targets, setTargets] = useState<TTarget[]>([]);

    const id_key_XMLToolBoxParam: number = getCodificationIdByCodes(CODIFICATION_TYPE.LESSON_METADATA, CODIFICATION_LESSON_METADATA.BLOCKLY_XML_TOOLBOX)


    const lesson: TLesson = location.state?.lesson;
    const [editingLesson, setEditingLesson] = useState<TLesson | undefined>(lesson);

    useEffect(() => {
        setIsLoading(true);

        if (lesson) {
            Promise.all([
                getCharacters(),
                getObstacles(),
                getScenes(),
                getTargets(),
                getLessonDetailsById(lesson.id)
            ])
                .then((results) => {
                    const [
                        charactersData,
                        obstaclesData,
                        scenesData,
                        targetsData,
                        lessonData
                    ] = results;

                    setCharacters(charactersData.data || []);
                    setObstacles(obstaclesData.data || []);
                    setScenes(scenesData.data || []);
                    setTargets(targetsData.data || []);
                    setEditingLesson(lessonData.data);
                })
                .catch((err) => {
                    message.error(err.response.data ?? err.message);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
        // eslint-disable-next-line
    }, [lesson]);

    const xmlRef = useRef<string>('');

    const save = async (): Promise<void> => {
        if (!editingLesson || saving) {
            return;
        }

        const codificationXML: TLessonMetadata = {
            id: editingLesson.metadata?.find(item => item.key_id === id_key_XMLToolBoxParam)?.id ?? 0,
            lesson_id: editingLesson.id,
            key_id: id_key_XMLToolBoxParam,
            value: xmlRef.current
        };

        // Mise à jour ou ajout du metadata
        const data: TLesson = {
            ...editingLesson,
            metadata: [
                ...(editingLesson?.metadata?.filter(item => item.key_id !== id_key_XMLToolBoxParam) || []),
                codificationXML
            ]
        };

        try {
            setSaving(true);
            const response = await saveLessonDetails(data);  // Attendre la fin de la promesse
            setEditingLesson(response.data);
            const savedXml = response.data.metadata?.find(item => item.key_id === id_key_XMLToolBoxParam)?.value;
            if (savedXml !== undefined) {
                xmlRef.current = savedXml;
            }
            message.success('Leçon enregistrée avec succès.');
        } catch (err: any) {
            message.error(err.response?.data ?? err.message ?? err);  // Gestion des erreurs
        } finally {
            setSaving(false);
        }
    };





    // Préparer les items pour Collapse 
    const items: CollapseProps['items'] = [
        {
            key: '1',
            label: 'Scene',
            children:
                <Scene
                    scenes={scenes}
                    editingLesson={editingLesson}
                    setEditingLesson={setEditingLesson}
                />,
        },
        {
            key: '2',
            label: 'Character',
            children:
                <Character
                    characters={characters}
                    editingLesson={editingLesson}
                    setEditingLesson={setEditingLesson}
                />,
        },
        {
            key: '3',
            label: 'Target',
            children:
                <Target
                    targets={targets}
                    editingLesson={editingLesson}
                    setEditingLesson={setEditingLesson}

                />,
        },
        {
            key: '4',
            label: 'Obstacle',
            children:
                <Obstacle
                    obstacles={obstacles}
                    editingLesson={editingLesson}
                    setEditingLesson={setEditingLesson}

                />,
        },
        {
            key: '5',
            label: 'Blocly fields',
            children:
                <BlocklyContent
                    blocklyContentFields={getCodificationsByCodeType(CODIFICATION_TYPE.BLOCKLY_CONTENT)}
                    editingLesson={editingLesson}
                    setEditingLesson={setEditingLesson}

                />,
        },
        {
            key: '6',
            label: 'Others',
            children:
                <MetaData
                    params={getCodificationsByCodeType(CODIFICATION_TYPE.LESSON_METADATA).filter(e => e.id !== id_key_XMLToolBoxParam)}
                    editingLesson={editingLesson}
                    setEditingLesson={setEditingLesson}

                />,
        },
    ];

    const navigate = useNavigate();
    
    const handleBackNavigation = () => {
        navigate(-1);
    };

    const toolboxBlocks = TLessonBlocklyContentTOBlocklyBlocks(editingLesson?.blocklycontent, getCodificationCodeById);

    return (
        <AppLayout 
            title={lesson.name} 
            loading={loading}
            onBackNavigation={handleBackNavigation}
            showBackButton={true}
        >
            <Row style={{ padding: '20px' }} gutter={[16, 16]}>
                <Col span={24}>
                    <Card >
                        <Collapse items={items} defaultActiveKey={['1']} />;
                    </Card>
                </Col>

                {isReferentialLoaded && toolboxBlocks.unknownFieldIds.length > 0 && (
                    <Col span={24}>
                        <Alert
                            type="error"
                            showIcon
                            message="Blocs inconnus du référentiel"
                            description={`Codifications absentes : ${toolboxBlocks.unknownFieldIds.join(', ')}. Ces blocs ne sont pas affichés dans la boîte à outils ; corriger la leçon ou le référentiel.`}
                        />
                    </Col>
                )}
                <Col span={24}>
                    <Card>
                        <BlocklyGame
                            toolbox={{
                                "contents": toolboxBlocks.blocks
                            }}
                            initialBlocksJsonOrXML={editingLesson?.metadata?.find(e => e.key_id === getCodificationIdByCodes(CODIFICATION_TYPE.LESSON_METADATA, CODIFICATION_LESSON_METADATA.BLOCKLY_XML_TOOLBOX))?.value}
                            character={TLessonCharacterTOTCharacter(editingLesson?.characters?.[0], getCodificationIdByCodes)}
                            obstacles={TLessonObstaclesTOObstacles(editingLesson?.obstacles)}
                            targets={TLessonTargetsTOTTargets(editingLesson?.targets, getCodificationIdByCodes)}
                            scene={TLessonSceneTOScene(editingLesson?.scene)}
                            xmlRef={xmlRef}
                            showGrid={stringToBool(editingLesson?.metadata?.find(e => e.key_id === getCodificationIdByCodes(CODIFICATION_TYPE.LESSON_METADATA, CODIFICATION_LESSON_METADATA.SHOW_GRID))?.value) || false}
                            drawResult={stringToBool(editingLesson?.metadata?.find(e => e.key_id === getCodificationIdByCodes(CODIFICATION_TYPE.LESSON_METADATA, CODIFICATION_LESSON_METADATA.DRAW_RESULT))?.value) || false}
                            autoReset={stringToBool(editingLesson?.metadata?.find(e => e.key_id === getCodificationIdByCodes(CODIFICATION_TYPE.LESSON_METADATA, CODIFICATION_LESSON_METADATA.AUTO_RESET))?.value) || false}
                            autoReach={stringToBool(editingLesson?.metadata?.find(e => e.key_id === getCodificationIdByCodes(CODIFICATION_TYPE.LESSON_METADATA, CODIFICATION_LESSON_METADATA.AUTO_REACH))?.value) || false}
                            respectOrder={stringToBool(editingLesson?.metadata?.find(e => e.key_id === getCodificationIdByCodes(CODIFICATION_TYPE.LESSON_METADATA, CODIFICATION_LESSON_METADATA.RESPECT_ORDER))?.value) || false}
                            mustReachAllTargets={stringToBool(editingLesson?.metadata?.find(e => e.key_id === getCodificationIdByCodes(CODIFICATION_TYPE.LESSON_METADATA, CODIFICATION_LESSON_METADATA.REACH_ALL_TARGET))?.value) || false}
                            canChangeXY={stringToBool(editingLesson?.metadata?.find(e => e.key_id === getCodificationIdByCodes(CODIFICATION_TYPE.LESSON_METADATA, CODIFICATION_LESSON_METADATA.CAN_CHANGE_XY))?.value) || false}
                            checkResult={(isValid: boolean) => { isValid && message.info('good') }}
                            cansave={true}
                            editionMode={true}

                        />
                    </Card>
                </Col>
            </Row>

            <Button
                type="default"
                shape="circle"
                icon={<SaveOutlined />}
                onClick={save}
                loading={saving}
                disabled={saving}
                style={{
                    position: 'fixed',
                    bottom: '24px',
                    right: '24px',
                    width: '56px',
                    height: '56px',
                    zIndex: 1000,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    color: '#1890ff'
                }}
            />

        </AppLayout>
    )
}

export default LessonBlocklyGame;
